import React, { useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  Button,
  IconButton,
} from '@mui/material';
import { Print, Close } from '@mui/icons-material';
import { useReactToPrint } from 'react-to-print';

const PrintPreviewSchedule = ({ schedule, open, onClose }) => {
  const printRef = useRef();

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Exam_Schedule_${schedule?.examType}_${schedule?.classesName}`,
  });

  return (
    <Dialog open={open} maxWidth='md' fullWidth onClose={onClose}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: 2,
          borderBottom: '1px solid #e0e0e0',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }}
      >
        <Typography variant='h6' component='h2' sx={{ color: '#fff' }}>
          Examination Schedule Preview
        </Typography>
        <IconButton onClick={onClose} size='small' sx={{ color: '#fff' }}>
          <Close />
        </IconButton>
      </Box>
      <DialogContent sx={{ backgroundColor: '#f5f7fa' }}>
        <Box sx={{ padding: 3 }} ref={printRef}>
          <Paper elevation={3} sx={{ padding: 3 }}>
            <Box sx={{ textAlign: 'center', marginBottom: 3 }}>
              <Typography
                variant='h4'
                component='h2'
                gutterBottom
                sx={{ color: '#667eea', fontWeight: 600 }}
              >
                Examination Schedule
              </Typography>
              <Typography
                variant='h5'
                component='h3'
                gutterBottom
                sx={{ color: '#764ba2', fontWeight: 500 }}
              >
                {schedule?.examType}
              </Typography>
              <Typography variant='body1' sx={{ color: '#4a5568' }}>
                <strong>Class:</strong>{' '}
                <Typography component='span' className='text-blue-500'>
                  {schedule?.departmentName}
                </Typography>
                <Typography component='span' className='text-red-500'>
                  {' '}
                  - {schedule?.classesName}
                </Typography>
                <Typography component='span' className='text-green-500'>
                  {schedule?.sectionName && ` - ${schedule.sectionName}`}
                </Typography>
              </Typography>
            </Box>

            <TableContainer component={Paper} sx={{ marginTop: 3 }}>
              <Table>
                <TableHead>
                  <TableRow
                    sx={{
                      background:
                        'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    }}
                  >
                    <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>
                      S.No
                    </TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>
                      Date
                    </TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>
                      Subject
                    </TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>
                      Time
                    </TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>
                      Max Marks
                    </TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>
                      Pass Marks
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {schedule?.subjects
                    ?.slice()
                    .sort((a, b) => {
                      const dateCompare = new Date(a.date) - new Date(b.date);
                      if (dateCompare !== 0) return dateCompare;

                      const timeA = a.startTime || '';
                      const timeB = b.startTime || '';
                      const startTimeCompare = timeA.localeCompare(timeB);
                      if (startTimeCompare !== 0) return startTimeCompare;

                      const endTimeA = a.endTime || '';
                      const endTimeB = b.endTime || '';
                      return endTimeA.localeCompare(endTimeB);
                    })
                    .map((subject, index) => (
                      <TableRow
                        key={subject.id}
                        sx={{
                          backgroundColor:
                            index % 2 === 0 ? '#f7fafc' : '#ffffff',
                          '&:hover': { backgroundColor: '#edf2f7' },
                        }}
                      >
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>
                          {new Date(subject.date).toLocaleDateString()}
                        </TableCell>
                        <TableCell sx={{ color: '#2d3748', fontWeight: 500 }}>
                          {subject.subjectName}
                        </TableCell>
                        <TableCell>
                          {subject.startTime && subject.endTime
                            ? `${subject.startTime} - ${subject.endTime}`
                            : 'Not specified'}
                        </TableCell>
                        <TableCell sx={{ color: '#38a169', fontWeight: 600 }}>
                          {subject.marks}
                        </TableCell>
                        <TableCell sx={{ color: '#d69e2e', fontWeight: 600 }}>
                          {subject.passMarks}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
                <TableFooter>
                  <TableRow sx={{ backgroundColor: '#edf2f7' }}>
                    <TableCell
                      colSpan={4}
                      align='right'
                      sx={{ fontWeight: 'bold', color: '#2d3748' }}
                    >
                      Total Marks:
                    </TableCell>
                    <TableCell
                      colSpan={2}
                      sx={{
                        fontWeight: 'bold',
                        color: '#667eea',
                        fontSize: '1.1rem',
                      }}
                    >
                      {schedule?.totalMarks}
                    </TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          padding: 2,
          borderTop: '1px solid #e0e0e0',
          backgroundColor: '#f7fafc',
        }}
      >
        <Button
          onClick={onClose}
          variant='outlined'
          sx={{ color: '#667eea', borderColor: '#667eea' }}
        >
          Close
        </Button>
        <Button
          onClick={handlePrint}
          variant='contained'
          startIcon={<Print />}
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #5568d3 0%, #653a8b 100%)',
            },
          }}
        >
          Print Schedule
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PrintPreviewSchedule;
